import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../state/hooks';
import { useShellProfile } from '../state/useShellProfile';
import type { ReportGenerationAuthorityScope } from './reportGenerationFieldAuthority';
import {
  buildReportGenerationFilterViewModel,
  type ReportGenerationFieldOption,
  type ReportGenerationFilterCondition,
} from './reportGenerationFiltering';
import {
  addReportGenerationFilterCondition,
  applyReportGenerationTemplateSelection,
  clearReportGenerationFilterSelections,
  markReportGenerationRunRequested,
  removeReportGenerationFilterCondition,
  replaceReportGenerationDefinitionSelection,
  resetReportGenerationDraft,
  setReportGenerationEntitySearch,
  setReportGenerationPrimarySortField,
  toggleReportGenerationEntitySelection,
  toggleReportGenerationFieldSelection,
  toggleReportGenerationPrimarySort,
  updateReportGenerationDatePreset,
  updateReportGenerationFilterCondition,
} from '../state/reportGenerationFilterSlice';

export const useReportGenerationFilterModel = (input?: {
  authorityScope?: ReportGenerationAuthorityScope | 'all';
  includeLegacy?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { profile, effectivePersona } = useShellProfile();
  const reportGenerationFilters = useAppSelector((state) => state.reportGenerationFilters);

  const viewModel = useMemo(() => buildReportGenerationFilterViewModel({
    profile,
    effectivePersona,
    state: reportGenerationFilters.filterState,
    entitySearch: reportGenerationFilters.entitySearch,
    selectedTemplateId: reportGenerationFilters.selectedTemplateId,
    authorityScope: input?.authorityScope,
    includeLegacy: input?.includeLegacy,
  }), [
    effectivePersona,
    input?.authorityScope,
    input?.includeLegacy,
    profile,
    reportGenerationFilters.entitySearch,
    reportGenerationFilters.filterState,
    reportGenerationFilters.selectedTemplateId,
  ]);

  const actions = useMemo(() => ({
    setEntitySearch: (query: string) => dispatch(setReportGenerationEntitySearch(query)),
    toggleEntity: (entityId: string) => dispatch(toggleReportGenerationEntitySelection(entityId)),
    toggleField: (fieldId: string) => dispatch(toggleReportGenerationFieldSelection(fieldId)),
    addCondition: (field: ReportGenerationFieldOption | null) => dispatch(addReportGenerationFilterCondition(field)),
    updateCondition: (conditionId: string, updates: Partial<ReportGenerationFilterCondition>) => dispatch(
      updateReportGenerationFilterCondition({ conditionId, updates }),
    ),
    removeCondition: (conditionId: string) => dispatch(removeReportGenerationFilterCondition(conditionId)),
    updateDateRange: (updates: Partial<typeof viewModel.state.dateRange>) => dispatch(updateReportGenerationDatePreset(updates)),
    setPrimarySortField: (field: ReportGenerationFieldOption | null) => dispatch(setReportGenerationPrimarySortField(field)),
    togglePrimarySortDirection: () => dispatch(toggleReportGenerationPrimarySort()),
    applyTemplate: (input: {
      templateId: string;
      source: 'gallery' | 'custom';
      entities: string[];
      fields: string[];
      conditions?: ReportGenerationFilterCondition[];
      sorts?: typeof reportGenerationFilters.filterState.sorts;
    }) => dispatch(
      applyReportGenerationTemplateSelection(input),
    ),
    replaceDefinition: (input: {
      filterState: typeof reportGenerationFilters.filterState;
      templateId?: string | null;
      source?: 'gallery' | 'custom' | null;
    }) => dispatch(replaceReportGenerationDefinitionSelection(input)),
    runReport: () => dispatch(markReportGenerationRunRequested()),
    clearAll: () => dispatch(clearReportGenerationFilterSelections()),
    startNewReport: () => dispatch(resetReportGenerationDraft()),
  }), [dispatch, viewModel.state.dateRange]);

  const nextConditionField = useMemo(() => viewModel.availableFields[0] ?? null, [viewModel.availableFields]);

  return {
    profile,
    effectivePersona,
    state: reportGenerationFilters,
    viewModel,
    nextConditionField,
    actions,
  };
};
